import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, View, VirtualizedList } from 'react-native';
import { CoreConfig } from '../../../CoreConfig';
import { component } from '../../Decorators/Component';
import { Configuration } from '../../../Domain/DTOs/Configuration/ConfigurationDTO';
import { H4View } from '../Typography/H4View';
import { LoadingPopUpView } from '../PopUps/LoadingPopUpView';
import { H6View } from '../Typography/H6View';
import { PressableButtonView } from '../Buttons/PressableButtonView';
import { AnimatedImageView } from '../Image/AnimatedImageView';
import { SimpleButtonView } from '../Buttons/SimpleButtonView';
import { getIsLandscape } from '../../Hooks/UseOrientation';
import { getDirection, useDirection } from '../../Hooks/UseDirection';
export function createSectionView(Component) {
    return component({
        provider: CoreConfig.provider,
        moduleName: CoreConfig.moduleName,
        componentName: Configuration.ComponentNames.section,
        style: (theme, mediaQuery, window, props) => {
            var _a, _b, _c, _d, _e, _f;
            let btn_top = '50%';
            const btn_height = ((_c = (_b = (_a = theme === null || theme === void 0 ? void 0 : theme.layouts) === null || _a === void 0 ? void 0 : _a[Configuration.ComponentNames.section]) === null || _b === void 0 ? void 0 : _b.button) === null || _c === void 0 ? void 0 : _c.height) ||
                ((_f = (_e = (_d = theme === null || theme === void 0 ? void 0 : theme.layouts) === null || _d === void 0 ? void 0 : _d[Configuration.ComponentNames.section]) === null || _e === void 0 ? void 0 : _e.buttonIcon) === null || _f === void 0 ? void 0 : _f.height) ||
                30;
            if (typeof props.btnTopPosition === 'string' || typeof props.btnTopPosition === 'number') {
                btn_top = props.btnTopPosition;
            }
            else if (typeof props.btnTopPosition === 'function') {
                btn_top = props.btnTopPosition(btn_height);
            }
            return {
                container: Object.assign({ maxWidth: '100%' }, props.containerStyle),
                header: {
                    flexDirection: 'row'
                },
                title: Object.assign({ paddingHorizontal: 10, flex: 1, fontWeight: 'bold', marginBottom: 0 }, props.titleStyle),
                actions: {
                    flexDirection: 'row'
                },
                scroll: Object.assign({}, Platform.select({
                    web: {
                        maxWidth: 100
                    }
                })),
                content: {
                    marginHorizontal: 0,
                    marginTop: 20,
                    flex: 1,
                    flexDirection: 'row',
                    alignItems: 'center'
                },
                flatList: {
                    flex: 1,
                },
                button: Object.assign(Object.assign({ display: 'none' }, getIsLandscape()
                    ? mediaQuery.lg({
                        display: 'flex'
                    })
                    : mediaQuery.md({
                        display: 'flex'
                    })), { position: 'absolute', justifyContent: 'center', alignItems: 'center', marginTop: props.btnTopPosition === undefined && typeof btn_height === 'number'
                        ? -btn_height
                        : undefined, top: btn_top }),
                leftButton: Object.assign({}, getDirection().value === 'ltr'
                    ? {
                        left: -50
                    }
                    : {
                        right: -50
                    }),
                rightButton: Object.assign({}, getDirection().value === 'ltr'
                    ? {
                        right: -50
                    }
                    : {
                        left: -50
                    }),
                buttonIcon: {
                    height: 30,
                },
                loadMoreBtn: {},
                loadMoreBtnText: {
                    fontSize: 14,
                    fontWeight: 'bold',
                },
                noItemsContainer: {
                    marginHorizontal: 0,
                    flex: 1,
                    justifyContent: 'center',
                    alignItems: 'center'
                },
                noItemsTitle: {
                    textAlign: 'center'
                },
                item: {
                    margin: 5
                },
                interactions: {
                    flex: 1,
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'flex-end'
                }
            };
        }
    })(({ onRef, style, assets, itemProps, itemStyle, hideButtons, contentStyle, interactions, values, type, title, noItemsTitle, loading, onPress, listOptions, onChange }) => {
        var _a, _b, _c, _d;
        const flatList = useRef(null);
        const [dir] = useDirection();
        const index = useRef(0);
        const [disabledNextBtn, setDisabledNextBtn] = useState(false);
        const [disabledPrevBtn, setDisabledPrevBtn] = useState(true);
        const [containerWidth, setContainerWidth] = useState(0);
        const [contentWidth, setContentWidth] = useState(0);
        const handlePress = useCallback((value) => onPress === null || onPress === void 0 ? void 0 : onPress(value), [onPress]);
        const onStartReach = useCallback(() => {
            setDisabledPrevBtn(true);
            setDisabledNextBtn(false);
        }, []);
        const onEndReach = useCallback(() => {
            setDisabledPrevBtn(false);
            setDisabledNextBtn(true);
        }, []);
        const onContentSizeChange = useCallback((width) => setContentWidth(width), []);
        const onLayout = useCallback((e) => setContainerWidth(e.nativeEvent.layout.width || 0), []);
        const handleItemsChanged = useCallback((info) => {
            var _a, _b, _c;
            const items = info.viewableItems || [];
            setDisabledPrevBtn((((_a = items[0]) === null || _a === void 0 ? void 0 : _a.index) || 0) <= 0);
            setDisabledNextBtn((((_b = items[items.length - 1]) === null || _b === void 0 ? void 0 : _b.index) || 0) >= values.length - 1);
            index.current = ((_c = info.viewableItems[0]) === null || _c === void 0 ? void 0 : _c.index) || 0;
        }, [values]);
        const handleNext = useCallback(() => {
            var _a;
            const nextIndex = values[index.current + 1]
                ? index.current + 1
                : 0;
            (_a = flatList.current) === null || _a === void 0 ? void 0 : _a.scrollToIndex({ index: nextIndex });
            onChange === null || onChange === void 0 ? void 0 : onChange(nextIndex);
        }, [flatList, index, values]);
        const handlePrev = useCallback(() => {
            var _a;
            const prevIndex = values[index.current - 1]
                ? index.current - 1
                : 0;
            (_a = flatList.current) === null || _a === void 0 ? void 0 : _a.scrollToIndex({ index: prevIndex });
        }, [flatList, index, values]);
        const keyExtractor = useCallback((item, index) => `${item.id || index}`, []);
        const showButtons = useMemo(() => !hideButtons && containerWidth < contentWidth, [containerWidth, contentWidth]);
        useEffect(() => {
            onRef === null || onRef === void 0 ? void 0 : onRef({
                scrollToIndex: (index) => {
                    var _a;
                    (_a = flatList.current) === null || _a === void 0 ? void 0 : _a.scrollToIndex({ index });
                    onChange === null || onChange === void 0 ? void 0 : onChange(index);
                }
            });
        }, [onRef]);
        const render = useCallback(({ item }) => (_jsx(View, { style: [style === null || style === void 0 ? void 0 : style.item, itemStyle], children: _jsx(Component, Object.assign({ value: item, type: type, onPress: handlePress }, itemProps)) })), [itemProps]);
        const start_icon = dir === 'ltr'
            ? 'si-arrow-left'
            : 'si-arrow-right';
        const end_icon = dir === 'ltr'
            ? 'si-arrow-right'
            : 'si-arrow-left';
        return (_jsxs(View, { style: style === null || style === void 0 ? void 0 : style.container, children: [!!title && (_jsxs(View, { style: style === null || style === void 0 ? void 0 : style.header, children: [_jsx(H4View, { textStyle: style === null || style === void 0 ? void 0 : style.title, children: title }), (interactions || []).length > 0 && (_jsx(View, { style: style === null || style === void 0 ? void 0 : style.interactions, children: interactions.map(interaction => (_jsx(PressableButtonView, Object.assign({}, interaction), interaction.action))) }))] })), values.length > 0
                    ? (_jsxs(View, { style: [style === null || style === void 0 ? void 0 : style.content, contentStyle], dir: 'ltr', children: [showButtons && (_jsx(SimpleButtonView, { onPress: handlePrev, containerStyle: Object.assign(Object.assign({}, style === null || style === void 0 ? void 0 : style.button), style === null || style === void 0 ? void 0 : style.leftButton), disabledOpacity: 0.6, disabled: disabledPrevBtn, children: _jsx(AnimatedImageView, { image: (_a = assets === null || assets === void 0 ? void 0 : assets.images) === null || _a === void 0 ? void 0 : _a.left_arrow_url, icon: start_icon, containerStyle: style === null || style === void 0 ? void 0 : style.buttonIcon, resizeMode: 'cover', color: (_b = style === null || style === void 0 ? void 0 : style.buttonIcon) === null || _b === void 0 ? void 0 : _b.color }) })), _jsx(VirtualizedList, Object.assign({ ref: flatList, onContentSizeChange: onContentSizeChange, onLayout: onLayout, inverted: dir === 'rtl' && Platform.OS === 'web', onViewableItemsChanged: handleItemsChanged, data: values, renderItem: render, viewabilityConfig: { itemVisiblePercentThreshold: 80 }, keyExtractor: keyExtractor, getItemCount: () => values.length, style: style === null || style === void 0 ? void 0 : style.flatList, getItem: (items, index) => items[index], showsHorizontalScrollIndicator: false, scrollEnabled: true, horizontal: true, onStartReached: onStartReach, onEndReached: onEndReach }, listOptions, Platform.OS === 'web' && {
                                initialNumToRender: values.length
                            })), showButtons && (_jsx(SimpleButtonView, { onPress: handleNext, containerStyle: Object.assign(Object.assign({}, style === null || style === void 0 ? void 0 : style.button), style === null || style === void 0 ? void 0 : style.rightButton), disabledOpacity: 0.6, disabled: disabledNextBtn, children: _jsx(AnimatedImageView, { image: (_c = assets === null || assets === void 0 ? void 0 : assets.images) === null || _c === void 0 ? void 0 : _c.right_arrow_url, icon: end_icon, containerStyle: style === null || style === void 0 ? void 0 : style.buttonIcon, color: (_d = style === null || style === void 0 ? void 0 : style.buttonIcon) === null || _d === void 0 ? void 0 : _d.color, resizeMode: 'cover' }) })), _jsx(LoadingPopUpView, { visible: loading })] }))
                    : (_jsxs(View, { style: [style === null || style === void 0 ? void 0 : style.content, style === null || style === void 0 ? void 0 : style.noItemsContainer], children: [_jsx(LoadingPopUpView, { visible: loading }), !loading && (_jsx(H6View, { textStyle: style === null || style === void 0 ? void 0 : style.noItemsTitle, children: noItemsTitle || 'No items' }))] }))] }));
    });
}
